import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { ScoreRing } from "@/components/OpportunityCard";
import { toast } from "sonner";
import { Lock, Check } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { LockedPath } from "@/components/LockedPath";
import { SprintDoneCard } from "@/components/SprintDoneCard";
import { ServicePicker } from "@/components/ServicePicker";
import { WelcomeCard } from "@/components/WelcomeCard";
const HANDS_ON = ["local-service", "flipping", "handmade-goods", "airbnb-cohosting", "gear-rental", "local-business-marketing", "maker-business", "job-broker"];

const SECTIONS = [
  { key: "why_it_fits", title: "WHY THIS FITS YOU" },
  { key: "what_you_need", title: "WHAT YOU NEED" },
  { key: "cost_breakdown", title: "WHAT IT COSTS" },
  { key: "safety_tips", title: "WHAT CAN GO WRONG" },
  { key: "where_to_start", title: "WHERE TO START" },
  { key: "tools", title: "TOOLS & WEBSITES" },
  { key: "learn_from", title: "WHO TO LEARN FROM" },
  { key: "how_to_win", title: "HOW TO WIN AT THIS" },
  { key: "what_to_expect", title: "WHAT TO EXPECT" },
  { key: "seven_days", title: "YOUR FIRST 7 DAYS" },
  { key: "thirty_days", title: "YOUR FIRST 30 DAYS" },
  { key: "path_to_replace_income", title: "PATH TO REPLACING YOUR JOB INCOME" },
];

export default function OpportunityDetail() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [sprint, setSprint] = useState({});
  const [stats, setStats] = useState(null);
  const [showTried, setShowTried] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  const loadOpp = () => {
    api.get(`/opportunities/${id}`).then(r => setOpp(r.data.opportunity));
    api.get(`/sprint/${id}`).then(r => setSprint(r.data.sprint || {}));
  };

  useEffect(() => {
    loadOpp();
    api.get(`/opportunities/${id}/stats`).then(r => setStats(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Scroll to a specific day when linked with a #sprint-day-row-N hash (e.g. from Your Plan).
  useEffect(() => {
    if (!opp || !location.hash) return;
    const el = document.querySelector(location.hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
  }, [opp, location.hash]);

  const submitTried = async () => {
    try {
      await api.post(`/opportunities/${id}/tried`, {});
      toast.success("You're on the path. Let's go.");
      setShowTried(false);
      const r = await api.get(`/opportunities/${id}/stats`);
      setStats(r.data);
    } catch { toast.error("Try again"); }
  };

  const toggleDay = async (day) => {
    const done = !sprint[`day_${day}`];
    setSprint(s => ({ ...s, [`day_${day}`]: done }));
    try { await api.post("/sprint", { opportunity_id: id, day, done }); }
    catch (e) {
      setSprint(s => ({ ...s, [`day_${day}`]: !done }));
      toast.error("Save failed");
    }
  };

  if (!opp) return <div className="p-8 text-gray-400">Loading…</div>;
  if (opp.locked) return <LockedPath opp={opp} />;

  const renderSection = (key) => {
    const v = opp[key];
    if (Array.isArray(v)) {
      if (key === "seven_days") {
        const locked = !!opp.sprint_locked;
        const titles = opp.seven_day_titles || [];
        return (
          <div data-testid={locked ? "sprint-locked" : "sprint-unlocked"}>
            {opp.sprint_pace && <p className="text-xs text-gray-400 mb-3" data-testid="sprint-pace">{opp.sprint_pace}</p>}
            <ol className="space-y-2">
              {v.map((task, i) => {
                const rowLocked = locked && i >= (opp.sprint_free_days ?? 0);
                return (
                <li key={i} id={`sprint-day-row-${i+1}`} className={`flex items-start gap-3 p-3 bg-[#0B0C0E] border rounded ${!rowLocked && locked ? "border-[#FD0F10]/50" : "border-[#2A2E39]"}`}>
                  {rowLocked ? (
                    <Lock size={16} className="text-gray-400 mt-1 shrink-0" data-testid={`sprint-lock-${i+1}`} />
                  ) : (
                    <input type="checkbox" checked={!!sprint[`day_${i+1}`]} onChange={() => toggleDay(i+1)} className="accent-[#FD0F10] scale-125 mt-1" data-testid={`sprint-day-${i+1}`} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-ui text-[10px] uppercase tracking-wider text-[#FD0F10] flex items-center gap-2">
                      DAY {i+1}
                      {!rowLocked && locked && <span className="px-1.5 py-0.5 border border-[#FD0F10] rounded text-[9px]" data-testid="sprint-free-badge">FREE</span>}
                    </div>
                    <div className="text-sm font-semibold text-white" data-testid={`sprint-title-${i+1}`}>{titles[i]}</div>
                    {rowLocked ? (
                      <div className="text-xs text-gray-500 mt-0.5 select-none" aria-hidden="true">Unlock Pro to see the exact task for this day.</div>
                    ) : (
                      <div className="text-sm text-gray-300 mt-0.5" data-testid={`sprint-task-${i+1}`}>{task}</div>
                    )}
                    {!user?.is_premium && (
                      <p className="text-[11px] text-gray-400 mt-1.5" data-testid={`sprint-stuck-${i+1}`}>
                        Stuck on a step? <Link to="/coach" className="underline hover:text-white">Your coach can walk you through it.</Link> With <Link to="/pro" className="underline text-[#FD0F10]">Pro</Link>, for $19 a month, you can talk it through out loud with your voice coach, anytime. No typing necessary.
                      </p>
                    )}
                  </div>
                </li>
                );
              })}
            </ol>
            {[1, 2, 3, 4, 5, 6, 7].every(n => sprint[`day_${n}`]) && <SprintDoneCard isPremium={!!user?.is_premium} />}
            {locked && !!sprint.day_2 && (
              <div className="mt-4 p-4 border border-[#FD0F10]/40 bg-[#FD0F10]/5 rounded flex flex-col sm:flex-row sm:items-center gap-3" data-testid="sprint-upgrade-prompt">
                <Lock size={18} className="text-[#FD0F10] shrink-0" />
                <div className="flex-1">
                  <div className="font-display font-bold text-white">Keep going, one day at a time.</div>
                  <p className="text-sm text-gray-300 mt-0.5">You've started your sprint. Keep going, one clear move a day, all seven days are yours.</p>
                </div>
                <Link to="/pro" className="hero-accent text-white font-bold px-4 py-2.5 rounded text-center whitespace-nowrap" data-testid="sprint-upgrade-btn">Unlock the full plan</Link>
              </div>
            )}
          </div>
        );
      }
      return <ul className="space-y-2 list-disc pl-5 text-gray-200 text-sm">{v.map((x,i)=><li key={i}>{x}</li>)}</ul>;
    }
    if (typeof v === "object" && v !== null) {
      return <div className="text-sm text-gray-200 space-y-2">{Object.entries(v).map(([k,val])=><div key={k}><span className="font-mono-ui text-[10px] uppercase tracking-wider text-gray-400">{k}: </span>{val}</div>)}</div>;
    }
    return <p className="text-gray-200 leading-relaxed text-sm sm:text-base">{v}</p>;
  };

  return (
    <div className="max-w-3xl mx-auto px-5 pt-6">
      <WelcomeCard opp={opp} hasDoneDays={Object.values(sprint).some(v => v === true)} />
      <Link to="/dashboard" className="text-xs font-mono-ui uppercase tracking-wider text-gray-400 hover:text-white" data-testid="back-dashboard">← Back</Link>
      <div className="mt-4 card-panel-elevated p-6">
        <div className="flex items-start gap-4">
          <ScoreRing score={opp.escape_score} size={72} />
          <div className="flex-1">
            <div className="eyebrow text-[#FD0F10]">ESCAPE SCORE · {opp.escape_score}%</div>
            <h1 className="font-display text-2xl sm:text-3xl font-black mt-1 leading-tight" data-testid="opp-title">{opp.title}</h1>
            <p className="text-gray-400 mt-1 text-sm">{opp.tagline}</p>
            {opp.fit_reason && <p className="text-xs text-[#FD0F10] mt-1.5" data-testid="opp-fit-reason">{opp.fit_reason}</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5">
          <Kv label="STARTUP" value={opp.startup_cost}/>
          <Kv label="DIFFICULTY" value={opp.difficulty}/>
          <Kv label="TIME" value={opp.time}/>
          <Kv label="INTERACTION" value={opp.interaction.replace("_", " ")}/>
          <Kv label="FIRST DOLLAR" value={opp.speed_first_dollar}/>
          <Kv label="POTENTIAL" value={opp.income_potential}/>
          <Kv label="SCALABILITY" value={`${opp.scalability}/10`}/>
          <Kv label="AI POTENTIAL" value={`${opp.ai_potential}/10`}/>
        </div>
        {opp.income_by_effort && <p className="mt-3 text-xs text-gray-300" data-testid="opp-income-by-effort">{opp.income_by_effort}</p>}
        {opp.earnings_note && <p className="mt-2 text-xs text-gray-300" data-testid="opp-earnings-note">{opp.earnings_note}</p>}
        <p className="mt-3 text-xs text-gray-400" data-testid="opp-results-disclaimer">Results depend on your effort. The more hours you put in, the faster it usually grows. Most people earn less at first, and that's normal.</p>
        {HANDS_ON.includes(opp.id) && <p className="mt-1 text-xs text-gray-400" data-testid="opp-hands-on-note">General information only. You're responsible for your own work, safety, and insurance.</p>}
      </div>

      <StartPathBar oppId={opp.id} key={`${opp.id}-${opp.service || ""}`} />
      {opp.id === "local-service" && <ServiceBox service={opp.service} onPicked={loadOpp} />}

      <div className="mt-6 space-y-4">
        {SECTIONS.filter(s => s.key !== "safety_tips" || opp.safety_tips).map(s => (
          <section key={s.key} className="card-panel p-5" data-testid={`section-${s.key}`}>
            <div className="eyebrow text-[#FD0F10] flex items-center gap-2">
              {s.title}
              {s.key === "seven_days" && opp.sprint_locked && <span className="font-mono-ui text-[9px] px-1.5 py-0.5 border border-[#FD0F10] rounded text-[#FD0F10]" data-testid="sprint-pro-badge">PRO</span>}
            </div>
            <div className="mt-3">{renderSection(s.key)}</div>
          </section>
        ))}
      </div>

      <div className="mt-8 mb-4 flex flex-col sm:flex-row gap-2 sm:flex-wrap">
        <Link to="/coach" className="hero-accent text-white font-bold px-4 py-3 rounded text-center" data-testid="ask-coach">Ask Escape Coach about this</Link>
        <Link to="/exit" className="border border-[#2A2E39] px-4 py-3 rounded hover:border-white text-center" data-testid="calc-exit">Calculate my Exit Number</Link>
        <button onClick={() => setShowTried(v => !v)} className="border border-[#FD0F10] text-[#FD0F10] px-4 py-3 rounded hover:bg-[#FD0F10] hover:text-white" data-testid="mark-tried-btn">
          I'm starting this
        </button>
      </div>
      <p className="mb-6 text-[11px] text-gray-500" data-testid="opp-fineprint">
        Income and timing figures are realistic possibilities, not guarantees. <Link to="/terms" className="underline hover:text-gray-400" data-testid="opp-terms-link">Full disclaimer</Link>.
      </p>

      {showTried && (
        <div className="card-panel-elevated p-5 mb-6" data-testid="tried-form">
          <div className="eyebrow text-[#FD0F10]">COMMIT TO THIS PATH</div>
          <p className="text-sm text-gray-200 mt-2">Tap the button below to mark this as your path. You'll get a nudge each week to keep momentum going.</p>
          <button onClick={submitTried} className="mt-3 hero-accent text-white font-bold px-5 py-2.5 rounded" data-testid="tried-submit">I'm in. Count me in.</button>
        </div>
      )}

      <div className="mb-6 card-panel p-5" data-testid="community-stats">
        <div className="eyebrow text-[#FD0F10]">YOU'RE NOT ALONE</div>
        {stats && stats.on_this_path > 0 ? (
          <p className="mt-2 text-gray-200">
            <span className="font-display text-2xl font-bold text-white">{stats.on_this_path}</span> {stats.on_this_path === 1 ? "person is" : "people are"} building this path with you. Keep going.
          </p>
        ) : (
          <p className="mt-2 text-sm text-gray-300">
            Be one of the first to start this path. Every big move begins with someone deciding to go first. That can be you today.
          </p>
        )}
      </div>
    </div>
  );
}

function StartPathBar({ oppId }) {
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/progress").then(r => setActive(r.data.progress?.chosen_path || "")).catch(() => setActive("")); }, [oppId]);
  if (active === null) return null;
  if (active === oppId) {
    return (
      <div className="mt-5 inline-flex items-center gap-2 px-3 py-2 rounded border border-[#FD0F10]/50 bg-[#FD0F10]/10 text-sm text-white" data-testid="active-path-badge">
        <Check size={15} className="text-[#FD0F10]" /> Your active path. Your coach and your sprint in My Work follow it.
      </div>
    );
  }
  const start = async () => {
    setBusy(true);
    try {
      await api.post("/path/start", { opportunity_id: oppId });
      setActive(oppId);
      toast.success(oppId === "local-service" ? "This is now your active path. Pick your service below." : "This is now your active path. Your sprint is in My Work.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start this path. Try again."); }
    setBusy(false);
  };
  return (
    <div className="mt-5 card-panel p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="start-path-bar">
      <p className="text-sm text-gray-300 flex-1">{active ? "Just looking? Your current path stays active until you start this one. Your old sprint stays saved in My Work." : "Like this one? Start it and your coach and sprint follow it."}</p>
      <button onClick={start} disabled={busy} className="hero-accent text-white font-bold px-5 py-2.5 rounded text-sm disabled:opacity-60" data-testid="start-path-btn">{busy ? "Starting…" : "Start this path"}</button>
    </div>
  );
}

const SWITCH_START = "/coach?start=" + encodeURIComponent("I'm thinking about switching to a different service.");

function ServiceBox({ service, onPicked }) {
  if (!service) return <div className="mt-6"><ServicePicker onPicked={onPicked} /></div>;
  return (
    <div className="mt-6 card-panel p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="service-chosen">
      <div className="flex-1">
        <div className="eyebrow text-[#FD0F10]">YOUR SERVICE</div>
        <div className="font-display text-xl font-black text-white mt-1" data-testid="service-chosen-name">{service}</div>
        <p className="text-xs text-gray-400 mt-1">Your 7-day sprint and your coach are tailored to this service.</p>
      </div>
      <Link to={SWITCH_START} className="border border-[#2A2E39] hover:border-white px-4 py-2.5 rounded text-sm text-center" data-testid="service-change-btn">Change service</Link>
    </div>
  );
}

function StatKv({ label, value }) {
  return (
    <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3">
      <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="font-display font-bold text-lg text-white mt-0.5">{value}</div>
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3">
      <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="text-sm text-gray-100 mt-0.5">{value}</div>
    </div>
  );
}
